import { themeStore } from "@/lib/theme-store"

import {
  createCustomThemeId,
  getCustomThemesFromStorage,
  MAX_CUSTOM_THEMES,
  sanitizeCustomSeed,
  type CustomThemeId,
  type CustomThemeSeed,
  writeCustomThemesToStorage,
} from "./custom-themes"
import { deriveTokens } from "./derive"
import {
  themes,
  DEFAULT_THEME_ID,
  type ThemeId as BuiltinThemeId,
  type ThemeSeed,
} from "./seeds"
import {
  decodeThemeShareCode,
  encodeThemeShareCode,
  type DecodedThemeSeedResult,
  type ThemeShareCodeErrorCode,
} from "./share-code"

export { themes, DEFAULT_THEME_ID } from "./seeds"
export type { ThemeSeed } from "./seeds"
export type { ThemeTokens } from "./derive"
export type { CustomThemeId, CustomThemeSeed } from "./custom-themes"
export { deriveTokens } from "./derive"
export { MAX_CUSTOM_THEMES } from "./custom-themes"
export {
  decodeThemeShareCode,
  encodeThemeShareCode,
  normalizeSeedForShare,
} from "./share-code"
export type {
  DecodedThemeSeedResult,
  ThemeShareCodeErrorCode,
} from "./share-code"

const STORAGE_KEY = "theme"
const STORAGE_MODE_KEY = "theme-mode"
const THEME_PROPS_ATTR = "data-theme-props"

export type ThemeId = BuiltinThemeId | CustomThemeId

function getBuiltinThemeIds(): BuiltinThemeId[] {
  return Object.keys(themes) as BuiltinThemeId[]
}

function getCustomThemesMap(
  customs: CustomThemeSeed[],
): Map<string, CustomThemeSeed> {
  return new Map(customs.map((seed) => [seed.id, seed]))
}

function isBuiltinThemeId(themeId: string): themeId is BuiltinThemeId {
  return themeId in themes
}

function isCustomThemeId(themeId: string): themeId is CustomThemeId {
  return themeId.startsWith("custom:")
}

function clearPrevThemeTokens(root: HTMLElement): void {
  const prev = root.getAttribute(THEME_PROPS_ATTR)
  if (!prev) return

  for (const prop of prev.split(",")) {
    root.style.removeProperty(prop)
  }
}

function applyTokensForSeed(seed: ThemeSeed | CustomThemeSeed): void {
  if (typeof document === "undefined") return

  const tokens = "tokens" in seed ? seed.tokens : deriveTokens(seed)
  const root = document.documentElement

  clearPrevThemeTokens(root)
  for (const [prop, value] of Object.entries(tokens)) {
    root.style.setProperty(prop, value)
  }

  root.setAttribute(THEME_PROPS_ATTR, Object.keys(tokens).join(","))
  root.classList.toggle("dark", seed.mode === "dark")
  root.classList.toggle("light", seed.mode !== "dark")
}

function bumpThemeCatalogVersion(): void {
  const current = themeStore.getState()
  themeStore.setState({
    ...current,
    themeCatalogVersion: current.themeCatalogVersion + 1,
  })
}

export function getDefaultThemeId(): ThemeId {
  if (typeof localStorage === "undefined") return DEFAULT_THEME_ID

  const stored = localStorage.getItem(STORAGE_KEY)
  if (!stored) return DEFAULT_THEME_ID

  if (isBuiltinThemeId(stored)) return stored

  if (isCustomThemeId(stored)) {
    const customThemes = getCustomThemesFromStorage()
    if (getCustomThemesMap(customThemes).has(stored)) {
      return stored
    }
  }

  localStorage.removeItem(STORAGE_KEY)
  localStorage.removeItem(STORAGE_MODE_KEY)
  return DEFAULT_THEME_ID
}

export function resolveThemeSeed(
  themeId: ThemeId,
): ThemeSeed | CustomThemeSeed | null {
  if (isBuiltinThemeId(themeId)) return themes[themeId]

  if (isCustomThemeId(themeId)) {
    const customThemes = getCustomThemesFromStorage()
    return getCustomThemesMap(customThemes).get(themeId) ?? null
  }

  return null
}

export function applyThemeSeed(seed: ThemeSeed): void {
  applyTokensForSeed(sanitizeCustomSeed(seed))
}

export function applyTheme(themeId: ThemeId): void {
  if (typeof document === "undefined") return

  const seed = resolveThemeSeed(themeId)
  if (!seed) {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(STORAGE_MODE_KEY)
    }

    if (themeId !== DEFAULT_THEME_ID) {
      applyTheme(DEFAULT_THEME_ID)
    }
    return
  }

  applyTokensForSeed(seed)

  try {
    localStorage.setItem(STORAGE_KEY, themeId)
    localStorage.setItem(STORAGE_MODE_KEY, seed.mode)
  } catch {}

  const current = themeStore.getState()
  themeStore.setState({
    ...current,
    themeId,
    resolvedTheme: seed.mode === "dark" ? "dark" : "light",
    isDark: seed.mode === "dark",
    source: "class",
  })
}

export function getCustomThemes(): CustomThemeSeed[] {
  return getCustomThemesFromStorage()
}

export function saveCustomTheme(
  seed: Omit<ThemeSeed, "id"> & { id?: CustomThemeId },
): CustomThemeSeed {
  const now = new Date().toISOString()
  const normalizedLabel = seed.label.trim()
  const id = seed.id ?? createCustomThemeId(normalizedLabel)
  const sanitizedSeed = sanitizeCustomSeed({
    ...seed,
    id,
    label: normalizedLabel,
  })

  const customTheme: CustomThemeSeed = {
    ...sanitizedSeed,
    id,
    createdAt: now,
    updatedAt: now,
    tokens: deriveTokens(sanitizedSeed),
  }

  const themesList = getCustomThemesFromStorage()
  const existingIdx = themesList.findIndex((item) => item.id === customTheme.id)

  if (existingIdx >= 0) {
    customTheme.createdAt = themesList[existingIdx]!.createdAt
    themesList[existingIdx] = customTheme
  } else {
    themesList.push(customTheme)
  }

  writeCustomThemesToStorage(themesList.slice(0, MAX_CUSTOM_THEMES))
  bumpThemeCatalogVersion()
  return customTheme
}

export function deleteCustomTheme(themeId: CustomThemeId): void {
  const themesList = getCustomThemesFromStorage()
  const next = themesList.filter((item) => item.id !== themeId)
  writeCustomThemesToStorage(next)

  if (getDefaultThemeId() === themeId) {
    applyTheme(DEFAULT_THEME_ID)
  }

  bumpThemeCatalogVersion()
}

export function getThemeIds(): ThemeId[] {
  const builtins = getBuiltinThemeIds()
  const customs = getCustomThemesFromStorage().map((theme) => theme.id)
  return [...builtins, ...customs]
}

export function getThemeLabel(id: ThemeId): string {
  if (isBuiltinThemeId(id)) return themes[id]?.label ?? id

  if (isCustomThemeId(id)) {
    return (
      getCustomThemesFromStorage().find((theme) => theme.id === id)?.label ?? id
    )
  }

  return id
}

export function getThemePrimaryColor(id: ThemeId): ThemeSeed["primary"] | null {
  const seed = resolveThemeSeed(id)
  return seed?.primary ?? null
}

export function isThemeNameTaken(label: string, excludeId?: string): boolean {
  const candidate = label.trim().toLowerCase()
  if (!candidate) return false

  const builtinTaken = getBuiltinThemeIds().some((id) => {
    if (id === excludeId) return false
    return themes[id].label.trim().toLowerCase() === candidate
  })

  if (builtinTaken) return true

  return getCustomThemesFromStorage().some((theme) => {
    if (theme.id === excludeId) return false
    return theme.label.trim().toLowerCase() === candidate
  })
}

export function getThemeSeedForBuilder(themeId: ThemeId): ThemeSeed {
  const seed = resolveThemeSeed(themeId) ?? themes[DEFAULT_THEME_ID]

  return {
    id: seed.id,
    label: seed.label,
    mode: seed.mode,
    base: { ...seed.base },
    primary: { ...seed.primary },
    accent: { ...seed.accent },
  }
}

export function validateThemeName(label: string): string | null {
  const trimmed = label.trim()
  if (trimmed.length < 2) return "Name must be at least 2 characters"
  if (trimmed.length > 24) return "Name must be at most 24 characters"
  return null
}

export function parseThemeId(value: string): ThemeId | null {
  if (isBuiltinThemeId(value)) return value
  if (isCustomThemeId(value)) return value
  return null
}

function getUniqueImportedThemeLabel(requestedLabel: string): string {
  const normalized = requestedLabel.trim() || "Imported Theme"
  const limitedBase = normalized.slice(0, 24)
  if (!isThemeNameTaken(limitedBase)) return limitedBase

  for (let i = 2; i <= 999; i++) {
    const suffix = ` ${i}`
    const candidate = `${limitedBase.slice(0, 24 - suffix.length)}${suffix}`
    if (!isThemeNameTaken(candidate)) return candidate
  }

  return `${limitedBase.slice(0, 20)}-${crypto.randomUUID().slice(0, 3)}`
}

export function encodeThemeShareCodeFromThemeId(
  themeId: ThemeId,
): string | null {
  const seed = resolveThemeSeed(themeId)
  if (!seed) return null
  return encodeThemeShareCode(seed)
}

export function decodeShareCodeToThemeSeed(code: string): ThemeSeed {
  const decoded = decodeThemeShareCode(code)
  if (!decoded.ok) {
    throw new Error(`Invalid theme share code: ${decoded.error}`)
  }
  return decoded.seed
}

export function createCustomThemeFromShareCode(
  code: string,
  label: string,
):
  | { ok: true; theme: CustomThemeSeed }
  | { ok: false; error: ThemeShareCodeErrorCode } {
  const decoded: DecodedThemeSeedResult = decodeThemeShareCode(code)
  if (!decoded.ok) return { ok: false, error: decoded.error }

  const nextLabel = getUniqueImportedThemeLabel(label)
  const { id: _id, label: _label, ...sharedWithoutIdentity } = decoded.seed
  const theme = saveCustomTheme({
    ...sharedWithoutIdentity,
    label: nextLabel,
  })

  return { ok: true, theme }
}
